/**
 * Share-link visit capture.
 *
 * A canonical Vitolier URL carrying `?s=VS…` was generated by a signed-in
 * member. The visit is recorded once per browser session per token so repeated
 * navigation, refreshes and back-button use never inflate the numbers.
 *
 * Privacy rules: the token is opaque, no user identifier is ever present in
 * the link, no third-party script is loaded, and only the landing path of a
 * public page is recorded.
 */
import { supabase } from "@/integrations/supabase/client";
import { SHARE_TOKEN_PARAM, isShareablePath } from "@/lib/sharing/registry";
import { normaliseShareToken } from "@/lib/sharing/share-client";
import { captureReferral, readCapturedReferral } from "@/lib/referral/capture";


const SEEN_KEY = "vitolier_share_visits";

function seen(): string[] {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY);
    const parsed = raw ? (JSON.parse(raw) as string[]) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function remember(key: string) {
  try {
    const next = Array.from(new Set([...seen(), key])).slice(-50);
    sessionStorage.setItem(SEEN_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

/** Stable-per-session opaque key used purely for de-duplication. */
function sessionKey(): string {
  const KEY = "vitolier_share_session";
  try {
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const fresh =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    return `s-${Date.now()}`;
  }
}

export interface ShareVisitResult {
  recorded: boolean;
  canonicalPath: string | null;
  available: boolean;
  /** The sharer's active referral code, when the link carries one. */
  referralToken: string | null;
}


/** Read the share token from a query string, if present and well-formed. */
export function readShareToken(search: string): string | null {
  try {
    return normaliseShareToken(new URLSearchParams(search).get(SHARE_TOKEN_PARAM));
  } catch {
    return null;
  }
}

/**
 * Record a visit that arrived on a shared link. Never throws — attribution
 * must never break a page load. Returns the canonical destination when the
 * shared content is still publicly available.
 */
export async function captureShareVisit(
  search: string,
  landingPath: string,
): Promise<ShareVisitResult> {
  const token = readShareToken(search);
  if (!token) return { recorded: false, canonicalPath: null, available: false, referralToken: null };

  const dedupeKey = `${token}:${landingPath}`;
  if (seen().includes(dedupeKey)) {
    return { recorded: false, canonicalPath: null, available: true, referralToken: null };
  }
  remember(dedupeKey);

  try {
    const { data, error } = await (supabase as any).rpc("record_content_share_visit", {
      _token: token,
      _landing_path: isShareablePath(landingPath) ? landingPath : null,
      _session_key: sessionKey(),
      _consent_state: "first_party_only",
      _conversion_type: null,
    });
    if (error) return { recorded: false, canonicalPath: null, available: false, referralToken: null };
    const payload = (data ?? {}) as {
      canonical_path?: string | null;
      available?: boolean;
      referral_token?: string | null;
    };
    const referralToken = payload.referral_token ?? null;

    // A shared link from a member also credits that member's referral, unless
    // the visitor already arrived on a referral of their own.
    if (referralToken && !readCapturedReferral()) {
      captureReferral(referralToken, "member_share_link");
    }

    return {
      recorded: true,
      canonicalPath: payload.canonical_path ?? null,
      available: payload.available !== false,
      referralToken,
    };
  } catch {
    return { recorded: false, canonicalPath: null, available: false, referralToken: null };
  }
}


/** Record a conversion (sign-up, membership start) against the arriving link. */
export async function recordShareConversion(token: string, conversionType: string): Promise<void> {
  const norm = normaliseShareToken(token);
  if (!norm) return;
  try {
    await (supabase as any).rpc("record_content_share_visit", {
      _token: norm,
      _landing_path: null,
      _session_key: `${sessionKey()}:${conversionType}`,
      _consent_state: "first_party_only",
      _conversion_type: conversionType,
    });
  } catch {
    /* ignore */
  }
}
